import api from 'lib/trpcClient';
import { useParams, Link } from 'react-router-dom';
import { z } from 'zod';
import { eisFirm as rules } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import PageSection from 'components/PageSection';
import WithdrawalFees from './WithdrawalFees';
import BankAccountDetailsForIncomingPayments from './BankAccountDetailsForIncomingPayments';
import Spacing from 'components/Spacing';
import BankAccountDetailsForFees from './BankAccountDetailsForFees';
import { Grid } from '@mui/material';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import { FIRM } from 'adminConstants/routes';
import { useNotification } from 'hooks/ui/useNotification';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';

export type BankAccountSettingsFormValues = z.infer<
  typeof rules.UpdateEisFirmBankAccountAndFeesSettingsInput
>;

const BankAccountSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const apiUtils = api.useUtils();
  const { success, error } = useNotification();
  const [canEditFirm] = useHasPermissions([RolePermission.EditFirm]);

  const { data: eisFirm } = api.eisFirm.getById.useQuery(
    { firmId: id },
    { enabled: !!id },
  );

  const { mutate: updateEisFirm, isPending: isUpdating } =
    api.eisFirm.updateBankAccountAndFeesSettings.useMutation({
      onSuccess: () => {
        success('Firm updated successfully.');
        apiUtils.eisFirm.getById.invalidate();
      },
      onError: (err) => {
        error(err?.message ?? 'Something went wrong.');
      },
    });

  const formMethods = useForm<BankAccountSettingsFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.UpdateEisFirmBankAccountAndFeesSettingsInput),
    values: {
      firmId: id ?? '',
      withdrawalFees: eisFirm?.withdrawalFees ?? [],
      withdrawalFeeText: eisFirm?.withdrawalFeeText ?? '',
      bankAccountForFeesExport: eisFirm?.bankAccountForFeesExport ?? {
        accountName: '',
        accountNumber: '',
        accountSortCode: '',
        reference: '',
      },
    },
    criteriaMode: 'all',
    reValidateMode: 'onSubmit',
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods;

  const onSubmit = async (data) => {
    await updateEisFirm({ ...data, id });
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'withdrawalFees',
  });

  return (
    <FormProvider {...formMethods}>
      <form>
        <Spacing size="lg">
          <PageSection title="Fund bank account details for incoming payments">
            <BankAccountDetailsForIncomingPayments firm={eisFirm} />
          </PageSection>
          <PageSection
            title="Fund bank account details for fee exports"
            showDivider
          >
            <BankAccountDetailsForFees control={control} />
          </PageSection>
          <PageSection title="Withdrawal fees" showDivider>
            <WithdrawalFees
              fees={fields ?? []}
              append={append}
              remove={remove}
              control={control}
              errors={errors}
            />
          </PageSection>
          <Grid size={{ xs: 4 }}>
            <ButtonGroup>
              <Link to={FIRM}>
                <Button variant="outlined">Cancel</Button>
              </Link>
              {canEditFirm ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  loading={isUpdating}
                >
                  Save
                </Button>
              ) : null}
            </ButtonGroup>
          </Grid>
        </Spacing>
      </form>
    </FormProvider>
  );
};

export default BankAccountSettings;
